import {
  ContextMenu,
  type ContextMenuAction,
  ErrorText,
  Pagination,
  Search,
  type SearchProps,
  Table as LibTable,
  TableActionsCell,
  TableEmptyState,
  TableErrorState,
  TableHeading,
  type TableHeadingProps,
  TableLoadingState,
  type TableProps,
  TableRow,
} from "@rothoblaas/design-system";
import { MouseEvent, ReactNode, useEffect, useState } from "react";
import { UseListFiltersReturn } from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import styles from "./Table.module.css";

type Props<T> = Omit<TableProps, "children" | "emptyState"> & {
  items?: T[];
  total?: number;
  head?: ReactNode;
  renderRow: (item: T) => ReactNode;
  rowActions?: (item: T) => ContextMenuAction[];
  onRowClick?: (item: T, event: MouseEvent<HTMLTableRowElement>) => void;
  filters?: UseListFiltersReturn;
  /** Forwarded to the heading's `Search`; its value is driven by `filters`. */
  search?: Partial<Pick<SearchProps, "placeholder" | "disabled" | "onChange">>;
  /** Rendered next to the search, as `TableHeading` children. */
  headingActions?: TableHeadingProps["children"];
  error?: boolean;
};

const Table = <T,>({
  items,
  total = -1,
  head,
  renderRow,
  rowActions,
  onRowClick,
  filters,
  search,
  headingActions,
  error,
  ...tableProps
}: Props<T>) => {
  const i18n = useI18n();
  const isSearchable = filters && filters.isSearchable;
  const page = filters?.state.page ?? 1;
  const limit = filters?.state.limit ?? 50;

  const fromItem = (page - 1) * limit + 1;
  const maxToItem = fromItem + limit - 1;

  const [data, setData] = useState<{ items: T[]; total: number }>();

  useEffect(() => {
    !!items && setData({ items, total });
  }, [items]);

  const toItem = Math.max(Math.min(maxToItem, data?.total ?? 0), 0);

  const status = error ? (
    <TableErrorState message={<ErrorText text={i18n.translation.common.errorLoadingData} />} />
  ) : !data?.items ? (
    <TableLoadingState />
  ) : data.total === 0 ? (
    <TableEmptyState message={i18n.translation.common.noItemsFound} />
  ) : undefined;

  return (
    <>
      {(isSearchable || headingActions) && (
        <TableHeading
          className={styles.heading}
          search={
            isSearchable && (
              <Search
                placeholder={search?.placeholder ?? i18n.translation.common.search}
                value={filters.state.q ?? ""}
                disabled={search?.disabled}
                debounceMs={0}
                onChange={(term) => {
                  if (!search?.disabled) {
                    search?.onChange?.(term);
                    filters.setSearchTerm(term);
                  }
                }}
              />
            )
          }
        >
          {headingActions}
        </TableHeading>
      )}

      <LibTable {...tableProps} emptyState={status}>
        {head && <thead>{head}</thead>}
        {!error && !!data?.items && (
          <tbody>
            {data.items.map((item, index) => (
              <TableRow
                key={`table_row_${index}`}
                onRowClick={onRowClick ? (event) => onRowClick(item, event) : undefined}
              >
                {renderRow(item)}
                {rowActions && (
                  <TableActionsCell>
                    {/* ContextMenu doesn't stop propagation, so a click would also trigger onRowClick. */}
                    <div onClick={(e) => e.stopPropagation()}>
                      <ContextMenu items={rowActions(item)} />
                    </div>
                  </TableActionsCell>
                )}
              </TableRow>
            ))}
          </tbody>
        )}
      </LibTable>

      {!error && !!data?.items && data.total > 0 && (
        <div className={styles.pagination}>
          <Pagination
            from={fromItem}
            to={toItem}
            total={data.total}
            canGoPrevious={page !== 1}
            canGoNext={toItem !== data.total}
            onPrevious={() => filters?.prevPage()}
            onNext={() => filters?.nextPage()}
          />
        </div>
      )}
    </>
  );
};

export default Table;
